// Behavioural classification of contactAIAgentSession rows.
//
// Replaces the binary (containment / escalation) framing — which penalises
// router bots whose job IS to hand off — with a six-way outcome derived
// from the session's own fields PLUS sibling-session + taskDetails joins.
//
// Classification rules per session (bot A):
//
//   0. session.sessionEndTime is null             → UNRESOLVED
//      (session still live during the window —
//      classifying it is premature; the next
//      rollup will skip these.)
//   1. interactionOutcome ABANDONED               → ABANDONED
//   2. interactionOutcome FAILED                  → FAILED
//   3. interactionOutcome ESCALATED               → ROUTED_TO_HUMAN
//   4. HANDLED + sibling AI session on same       → ROUTED_TO_AI
//      interactionId that started after this
//      bot's sessionEndTime (the next bot in
//      the orchestration flow picked it up —
//      THIS bot's job was to route)
//   5. HANDLED + no next AI sibling + joined      → ROUTED_TO_HUMAN
//      taskDetails has lastAgent.name populated   (the ONLY populated-only-when-
//                                                 a-human-picks-up signal; we
//                                                 used to also treat lastQueue
//                                                 as a signal but it fires on
//                                                 the PRE-bot IVR queue too,
//                                                 which created false positives)
//   6. HANDLED + no next AI sibling + no human    → RESOLVED_WITH_TOOLS
//      landing + totalActionCompleted > 0 OR
//      fulfillmentCount > 0
//   7. HANDLED + no next AI sibling + no human    → RESOLVED_NO_TOOLS
//      landing + no action/fulfillment               (talked and stopped — info/FAQ,
//                                                     OR a bot stuck in a loop)
//
// Role inference (bot-level): classify every session, roll up per bot,
// label the bot by its dominant pattern. Thresholds tunable below.
//
// NO new MCP calls — the sessions + tasks are already fetched by
// useReportWindow for the open date window; classification is pure
// client-side projection.

export const CLASSIFICATIONS = Object.freeze({
  ROUTED_TO_AI: 'routed-to-ai',
  ROUTED_TO_HUMAN: 'routed-to-human',
  RESOLVED_WITH_TOOLS: 'resolved-with-tools',
  RESOLVED_NO_TOOLS: 'resolved-no-tools',
  ABANDONED: 'abandoned',
  FAILED: 'failed',
  UNRESOLVED: 'unresolved', // session.sessionEndTime missing — skip in rollups
});

// Classification metadata used by every report that renders the breakdown.
// `success`: counts toward the bot's success rate.
// `taskConcernColor`: color used for `role === 'task'` only — a task bot
// resolving-no-tools is a yellow flag, but for routers/transfer-focused/hybrid
// bots the same outcome is just neutral info (grey).
export const CLASSIFICATION_META = Object.freeze({
  [CLASSIFICATIONS.ROUTED_TO_AI]:         { label: 'Routed to sub-bot',   color: 'cyan',   success: true,  taskConcernColor: 'cyan' },
  [CLASSIFICATIONS.RESOLVED_WITH_TOOLS]:  { label: 'Resolved with tools', color: 'teal',   success: true,  taskConcernColor: 'teal' },
  [CLASSIFICATIONS.RESOLVED_NO_TOOLS]:    { label: 'Resolved no tools',   color: 'gray',   success: false, taskConcernColor: 'yellow' },
  [CLASSIFICATIONS.ROUTED_TO_HUMAN]:      { label: 'Routed to human',     color: 'blue',   success: false, taskConcernColor: 'blue' },
  [CLASSIFICATIONS.ABANDONED]:            { label: 'Abandoned',           color: 'orange', success: false, taskConcernColor: 'orange' },
  [CLASSIFICATIONS.FAILED]:               { label: 'Failed',              color: 'red',    success: false, taskConcernColor: 'red' },
  [CLASSIFICATIONS.UNRESOLVED]:           { label: 'Unresolved (live)',   color: 'gray',   success: false, taskConcernColor: 'gray' },
});

// Threshold at which a single classification type's share flips a bot into
// a specific role tag. 0.7 = "if 70% of this bot's sessions look like X,
// call it an X-bot". Hybrid = none of the pure roles reach the threshold.
const ROLE_THRESHOLD = 0.7;

// Minimum resolved sessions required before a role is anything other than
// Unknown. Stops a bot with 1-2 sessions from being confidently labeled
// Router when the sample isn't meaningful.
export const MIN_SESSIONS_FOR_ROLE = 5;

export const ROLES = Object.freeze({
  ROUTER:           'router',
  TASK:             'task',
  TRANSFER_FOCUSED: 'transfer-focused',
  HYBRID:           'hybrid',
  UNKNOWN:          'unknown',
});

export const ROLE_META = Object.freeze({
  [ROLES.ROUTER]:           { label: 'Router',           color: 'cyan',  description: '≥70% of resolved sessions handed off to another AI Agent — triage/orchestration bot.' },
  [ROLES.TASK]:             { label: 'Task',             color: 'teal',  description: '≥70% of resolved sessions completed with tool invocations — terminal task bot.' },
  [ROLES.TRANSFER_FOCUSED]: { label: 'Transfer-focused', color: 'blue',  description: '≥70% of resolved sessions routed to a human — reception/screening bot.' },
  [ROLES.HYBRID]:           { label: 'Hybrid',           color: 'grape', description: 'Mix of routing + resolution — handles some itself, hands off some.' },
  [ROLES.UNKNOWN]:          { label: 'Unknown',          color: 'gray',  description: `Not enough resolved sessions to classify (n < ${MIN_SESSIONS_FOR_ROLE}).` },
});

/**
 * Classify every session in the window.
 *
 * @param {Array} sessions - contactAIAgentSession rows (useReportWindow.data.sessions)
 * @param {Array} tasks    - taskDetails rows             (useReportWindow.data.tasks)
 * @returns {Array<{ session, classification }>}
 */
export function classifySessions(sessions, tasks) {
  const taskByInteractionId = new Map();
  for (const t of tasks ?? []) {
    if (t?.id) taskByInteractionId.set(t.id, t);
  }

  // Group sibling sessions by interactionId so we can detect the "next bot"
  // in a multi-bot orchestration flow (routing success signal).
  const siblingsByInteractionId = new Map();
  for (const s of sessions ?? []) {
    const key = s?.interactionId;
    if (!key) continue;
    if (!siblingsByInteractionId.has(key)) siblingsByInteractionId.set(key, []);
    siblingsByInteractionId.get(key).push(s);
  }
  for (const list of siblingsByInteractionId.values()) {
    list.sort((a, b) => toMs(a.sessionStartTime) - toMs(b.sessionStartTime));
  }

  return (sessions ?? []).map((s) => ({
    session: s,
    classification: classifyOne(
      s,
      siblingsByInteractionId.get(s?.interactionId) ?? [],
      taskByInteractionId.get(s?.interactionId),
    ),
  }));
}

function classifyOne(session, siblings, task) {
  // Guard: session still live. Skip — classifying premature.
  if (session?.sessionEndTime == null) return CLASSIFICATIONS.UNRESOLVED;

  const outcome = session?.interactionOutcome;

  if (outcome === 'ABANDONED') return CLASSIFICATIONS.ABANDONED;
  if (outcome === 'FAILED')    return CLASSIFICATIONS.FAILED;
  if (outcome === 'ESCALATED') return CLASSIFICATIONS.ROUTED_TO_HUMAN;

  // From here, outcome is HANDLED/COMPLETED/other. Look at the orchestration
  // picture across sibling sessions and the taskDetails join to decide
  // routing-success vs resolution vs silent-handoff-to-human.

  const endTime = toMs(session?.sessionEndTime);
  const nextAiSibling = siblings.find((sib) =>
    sib?.sessionId !== session?.sessionId &&
    toMs(sib?.sessionStartTime) >= endTime,
  );
  if (nextAiSibling) return CLASSIFICATIONS.ROUTED_TO_AI;

  // No next AI sibling. The ONLY reliable "a human picked up" signal is
  // taskDetails.lastAgent?.name — populated only when an agent is assigned
  // to the contact. We previously also treated lastQueue.name as a signal
  // but it fires on the pre-bot IVR queue too, creating false positives
  // (bots that contained successfully were being marked ROUTED_TO_HUMAN).
  if (task?.lastAgent?.name) return CLASSIFICATIONS.ROUTED_TO_HUMAN;

  // Terminal bot. Did it do real work?
  const actions = session?.totalActionCompleted ?? 0;
  const fulfils = session?.fulfillmentCount ?? 0;
  if (actions > 0 || fulfils > 0) return CLASSIFICATIONS.RESOLVED_WITH_TOOLS;
  return CLASSIFICATIONS.RESOLVED_NO_TOOLS;
}

/**
 * Roll classified sessions up per bot (optionally per bot × day) and attach
 * an inferred role tag + confidence per bucket.
 *
 * UNRESOLVED sessions are counted in `total` but NOT in `resolvedTotal`;
 * role inference uses resolvedTotal so a bot with many live sessions
 * doesn't get a weird role tag mid-window.
 *
 * @param {Array} classified - output of classifySessions()
 * @param {Object} opts
 * @param {boolean} opts.byDay - include day in the group key
 * @returns {Array<{ key, name, day?, total, resolvedTotal, counts, successRate, role, roleConfidence }>}
 */
export function rollupByBot(classified, opts = {}) {
  const { byDay = false } = opts;
  const buckets = new Map();

  for (const { session, classification } of classified) {
    const name = session?.agentName ?? '(unnamed)';
    const day = byDay ? isoDay(session?.sessionStartTime) : null;
    if (byDay && !day) continue;

    const key = byDay ? `${day}|${name}` : name;
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        name,
        day,
        total: 0,
        resolvedTotal: 0,
        counts: freshCounts(),
      });
    }
    const b = buckets.get(key);
    b.total += 1;
    b.counts[classification] = (b.counts[classification] ?? 0) + 1;
    if (classification !== CLASSIFICATIONS.UNRESOLVED) b.resolvedTotal += 1;
  }

  return [...buckets.values()].map((b) => {
    const successCount =
      (b.counts[CLASSIFICATIONS.ROUTED_TO_AI] ?? 0) +
      (b.counts[CLASSIFICATIONS.RESOLVED_WITH_TOOLS] ?? 0);
    const { role, confidence } = inferRole(b.counts, b.resolvedTotal);
    return {
      ...b,
      successRate: b.resolvedTotal ? Math.round((successCount / b.resolvedTotal) * 100) : 0,
      role,
      roleConfidence: confidence,
    };
  });
}

/**
 * Infer the dominant role for a bot given its classification counts.
 *
 * Returns { role, confidence } where confidence is 0-1:
 *   confidence = (dominantShare - secondShare) × sampleFactor
 *     dominantShare — share of resolvedTotal held by the top classification
 *     secondShare   — share held by the runner-up (0 if there's only one)
 *     sampleFactor  — resolvedTotal / (resolvedTotal + 10), caps near 1.0 as
 *                      sample grows (10-session boost means a bot with 10
 *                      sessions has ~0.5 sample factor, 50 sessions ~0.83).
 *
 * confidence < 0.3 is a reasonable "take with salt" threshold for UI tooltips.
 */
export function inferRole(counts, resolvedTotal) {
  if (!resolvedTotal || resolvedTotal < MIN_SESSIONS_FOR_ROLE) {
    return { role: ROLES.UNKNOWN, confidence: 0 };
  }
  const shares = [
    [CLASSIFICATIONS.ROUTED_TO_AI,        (counts[CLASSIFICATIONS.ROUTED_TO_AI] ?? 0) / resolvedTotal,        ROLES.ROUTER],
    [CLASSIFICATIONS.RESOLVED_WITH_TOOLS, (counts[CLASSIFICATIONS.RESOLVED_WITH_TOOLS] ?? 0) / resolvedTotal, ROLES.TASK],
    [CLASSIFICATIONS.ROUTED_TO_HUMAN,     (counts[CLASSIFICATIONS.ROUTED_TO_HUMAN] ?? 0) / resolvedTotal,     ROLES.TRANSFER_FOCUSED],
  ].sort((a, b) => b[1] - a[1]);

  const [, dominantShare, dominantRole] = shares[0];
  const [, secondShare = 0] = shares[1] ?? [];

  const sampleFactor = resolvedTotal / (resolvedTotal + 10);
  const spread = Math.max(0, dominantShare - secondShare);
  const confidence = Math.min(1, spread * sampleFactor * 2); // ×2 so 50% spread → 1.0 at infinite N

  if (dominantShare >= ROLE_THRESHOLD) {
    return { role: dominantRole, confidence };
  }
  return { role: ROLES.HYBRID, confidence };
}

/**
 * For the Routing Flow report: compute (fromBot → toBot) transitions by
 * walking adjacent-sibling pairs on each interactionId. If a bot session
 * is terminal (no next AI sibling) but the task landed on a human (via
 * taskDetails.lastAgent.name — same strong signal as classifyOne), emit
 * a (fromBot → __HUMAN__) edge so the sankey covers 100% of handoffs.
 *
 * UNRESOLVED-source sessions are skipped (no handoff decision made yet).
 *
 * @returns {Array<{ fromBot, toBot, count }>}
 */
export function transitionsFromSessions(sessions, tasks) {
  const taskByInteractionId = new Map();
  for (const t of tasks ?? []) if (t?.id) taskByInteractionId.set(t.id, t);

  const siblingsByInteractionId = new Map();
  for (const s of sessions ?? []) {
    const key = s?.interactionId;
    if (!key) continue;
    if (!siblingsByInteractionId.has(key)) siblingsByInteractionId.set(key, []);
    siblingsByInteractionId.get(key).push(s);
  }
  for (const list of siblingsByInteractionId.values()) {
    list.sort((a, b) => toMs(a.sessionStartTime) - toMs(b.sessionStartTime));
  }

  const edges = new Map(); // key = `${fromBot}||${toBot}`
  const bump = (fromBot, toBot) => {
    const key = `${fromBot}||${toBot}`;
    edges.set(key, (edges.get(key) ?? 0) + 1);
  };

  for (const list of siblingsByInteractionId.values()) {
    for (let i = 0; i < list.length; i += 1) {
      const cur = list[i];
      // Skip unresolved (sessionEndTime null) — no handoff decision made yet.
      if (cur?.sessionEndTime == null) continue;
      const curBot = cur?.agentName ?? '(unnamed)';
      const next = list[i + 1];

      if (next) {
        bump(curBot, next?.agentName ?? '(unnamed)');
        continue;
      }

      // Terminal in the AI chain. If a human picked up, record that too.
      if (cur?.interactionOutcome === 'ESCALATED') {
        bump(curBot, '__HUMAN__');
        continue;
      }
      const task = taskByInteractionId.get(cur?.interactionId);
      if (task?.lastAgent?.name) {
        bump(curBot, '__HUMAN__');
      }
    }
  }

  return [...edges.entries()].map(([k, count]) => {
    const [fromBot, toBot] = k.split('||');
    return { fromBot, toBot, count };
  });
}

/**
 * Convenience: run classify + rollup once and return both — so Analytics
 * can compute once and prop-drill the results to every report instead of
 * each report re-classifying the same window independently.
 */
export function analyzeWindow(sessions, tasks) {
  const classified = classifySessions(sessions, tasks);
  const perBot = rollupByBot(classified);
  const perBotDay = rollupByBot(classified, { byDay: true });
  return { classified, perBot, perBotDay };
}

// ---- helpers ----

function freshCounts() {
  const o = {};
  for (const v of Object.values(CLASSIFICATIONS)) o[v] = 0;
  return o;
}

function toMs(ts) {
  if (ts == null) return 0;
  const n = typeof ts === 'number' ? ts : Number(ts);
  return Number.isFinite(n) ? n : 0;
}

function isoDay(ts) {
  if (ts == null) return null;
  const n = typeof ts === 'number' ? ts : Number(ts);
  const d = !Number.isNaN(n) && n > 1e12 ? new Date(n) : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}
